/* eslint-disable */
import { useEffect, useState, useRef } from 'react';
import uniqueId from 'uniqid';
// import { StoreContext } from '../context/store-context';

export const useWindowEventListener = (windowEvent, handlerCallback, options = false) => {
	useEffect(() => {
		const isBrowser = typeof window !== 'undefined';

		if (isBrowser) {
			window.addEventListener(windowEvent, handlerCallback, options);
		}

		return () => {
			window.removeEventListener(windowEvent, handlerCallback, options);
		};
	}, [windowEvent, handlerCallback, options]);
};

export const useAddKeyProp = (nonGuidItems, prefix) => {
	const [keyedItems, setKeyedItems] = useState();

	useEffect(() => {
		nonGuidItems
		&& nonGuidItems.length > 0
		&& setKeyedItems((
			nonGuidItems.map(item => {
				return {
					key: uniqueId(prefix),
					...item,
				};
			})
		));
	}, [nonGuidItems, prefix]);

	return keyedItems;
};

export const useKeypressHandler = (key, action) => {
	useEffect(() => {
		function onKeyup(e) {
			if (e.code === key && typeof action === 'function') action(e);
		}
		window.addEventListener('keyup', onKeyup);

		return () => {
			return window.removeEventListener('keyup', onKeyup);
		};
	}, [key, action]);
};

// Local Storage Hook

// event emitter to share local storage data across different components
const storageEvent = new EventTarget();

export function useLocalStorage(key, initialValue, callBackFunction) {
	const [value, setValue] = useState(() => {
		try {
			const item = window.localStorage.getItem(key);
			return item !== null ? JSON.parse(item) : initialValue;
		} catch {
			return initialValue;
		}
	});

	// Write to localStorage
	useEffect(() => {
		window.localStorage.setItem(key, JSON.stringify(value));
	}, [key, value]);

	// Listen to external tab updates
	useEffect(() => {
		function onStorage(event) {
			if (event.key === key) {
				try {
					setValue(prev => (event.newValue ? JSON.parse(event.newValue) : prev));
					callBackFunction?.();
				} catch {}
			}
		}

		window.addEventListener('storage', onStorage);
		return () => window.removeEventListener('storage', onStorage);
	}, [key, callBackFunction]);

	// notify other components
	useEffect(() => {
		try {
			storageEvent.dispatchEvent(
				new CustomEvent(`localstore:${ key }`, { detail: value })
			);
		} catch {}
	}, [key, value]);

	// Listen for updates from other components
	useEffect(() => {
		function handler(e) {
			setValue(() => e.detail);
		}

		storageEvent.addEventListener(`localstore:${ key }`, handler);
		return () => storageEvent.removeEventListener(`localstore:${ key }`, handler);
	}, [key]);

	return {value, setValue};
}

/**
 * Scroll hint for scrollable panels (drawers, flyouts, modals).
 * Shows a hint while `isActive` and the panel can scroll further down.
 *
 * Usage:
 *   const { panelRef, showScrollHint, updateScrollHint, handleScrollHintClick } = useScrollHint(isOpen);
 *   <div ref={panelRef} onScroll={updateScrollHint}>...</div>
 *   <button className={showScrollHint ? 'is-visible' : ''} onClick={handleScrollHintClick}>...</button>
 */
export const useScrollHint = isActive => {
	const panelRef = useRef(null);
	const [showScrollHint, setShowScrollHint] = useState(false);

	// show the scroll hint while the panel can scroll and isn't at the bottom
	const updateScrollHint = () => {
		const el = panelRef.current;
		if (!el) return;

		const scrollable = el.scrollHeight > el.clientHeight + 1;
		const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 4;

		setShowScrollHint(scrollable && !atBottom);
	};

	useEffect(() => {
		if (!isActive) {
			setShowScrollHint(false);
			return undefined;
		}

		// measure once the panel has laid out
		const timer = setTimeout(updateScrollHint, 50);
		window.addEventListener('resize', updateScrollHint);

		return () => {
			clearTimeout(timer);
			window.removeEventListener('resize', updateScrollHint);
		};
	}, [isActive]);

	const handleScrollHintClick = () => {
		const el = panelRef.current;
		if (el) el.scrollBy({ top: el.clientHeight * 0.8, behavior: 'smooth' });
	};

	return {
		panelRef, showScrollHint, updateScrollHint, handleScrollHintClick,
	};
};
